// Copyright 2016 Sorama B.V.

export class Splice<T> {
    index: number;
    deleteCount: number;
    replaceItems: T[];
}

export class ArrayExtensions {
    public static equals<T>(a: T[], b: T[]): boolean {
        if (a === b) return true;
        if (a == null || b == null) return false;
        if (a.length != b.length) return false;

        for (let i = 0; i < a.length; ++i) {
            if (a[i] !== b[i]) return false;
        }

        return true;
    }

    public static toUrlString<T>(array: T[], serializeItem: (value: T) => string): string {
        let str = "";
        for (let i = 0; i < array.length; i++) {
            str += serializeItem(array[i]);
            if (i != array.length - 1) str += " ";
        }

        return str;
    }

    public static fromUrlString<T>(str: string, deserializeItem: (str: string) => T): T[] {
        if (!str || typeof str !== "string") return [];

        const items = str.split(" ");
        return items.map((x) => deserializeItem(x));
    }

    /**
     * Compares entries of an array and removes duplicates.
     * @param array The array to remove duplicates from.
     * @param comparer A function comparing two entries for equality. Should return true if two entries can be considered duplicates, false otherwise.
     */
    public static unique<T>(array: T[], comparer: (a: T, b: T) => boolean): T[] {
        const deduplicatedArray: T[] = [];

        for (let i = 0; i < array.length; i++) {
            const item = array[i];

            if (!deduplicatedArray.some((x) => comparer(item, x))) {
                deduplicatedArray.push(item);
            }
        }

        return deduplicatedArray;
    }

    public static findSplices<T>(fromArray: T[], toArray: T[], equals: (a: T, b: T) => boolean): Splice<T>[] {
        // We have an implementation, find splices
        const addItems = <T[]>[];
        const remItems = <Splice<T>[]>[];

        // First check if all new items are accounted for
        for (let i = 0; i < toArray.length; i++) {
            const item = toArray[i];
            const match = fromArray.some((x) => equals(x, item));
            if (!match) {
                addItems.push(item);
            }
        }

        // Now check if all existing items are in the new list
        let firstRem = -1;
        let remCount = 0;
        let indexOffset = 0;
        for (let i = 0; i < fromArray.length; i++) {
            const item = fromArray[i];
            const match = toArray.some((x) => equals(x, item));
            if (!match) {
                if (firstRem === -1) {
                    firstRem = i;
                }
                remCount++;
            } else if (remCount != 0) {
                remItems.push({
                    index: firstRem - indexOffset,
                    deleteCount: remCount,
                    replaceItems: [],
                });

                indexOffset += remCount;
                firstRem = -1;
                remCount = 0;
            }
        }

        if (remItems.length > 0) {
            remItems[remItems.length - 1].replaceItems = addItems;
        } else if (addItems.length > 0) {
            remItems.push({
                index: fromArray.length,
                deleteCount: 0,
                replaceItems: addItems,
            });
        }

        return remItems;
    }

    public static find<T>(array: T[], predicate: (value: T, index: number, obj: T[]) => boolean, thisArg?: unknown): T {
        return <T>Array.prototype.find.apply(array, [predicate, thisArg]);
    }

    public static findFirstAndLastIndex<T>(
        array: T[],
        predicate: (element: T) => boolean,
        lastPredicate?: (element: T) => boolean
    ): { firstIndex: number; lastIndex: number } {
        let firstIndex = -1;
        let lastIndex = -1;
        for (let i = 0; i < array.length; i++) {
            if (firstIndex == -1 && predicate(array[i])) firstIndex = i;

            if (lastPredicate ? lastPredicate(array[i]) : predicate(array[i])) lastIndex = i;
        }

        return { firstIndex: firstIndex, lastIndex: lastIndex };
    }

    public static fastIndexOf<T>(
        array: T[],
        predicate: (value: T, index?: number, array?: T[]) => number,
        approx?: boolean
    ): number {
        // Start half way through the array
        let size = array.length / 2;
        let realIndex = size;
        let index = Math.floor(realIndex);

        const iters = Math.floor(Math.log2(size)) + 1;
        for (let i = 0; i <= iters; i++) {
            const res = predicate(array[index], index, array);
            if (res === 0) return index;

            size /= 2;
            realIndex += res < 0 ? -size : size;
            index = Math.floor(realIndex);
        }

        return approx ? index : -1;
    }

    public static fastFindClosestValueIndex<T>(values: T[], target: number, transformer: (value: T) => number): number {
        if (values.length === 0) return -1;

        return ArrayExtensions.fastIndexOf(
            values,
            (v, index) => {
                const value = transformer(v);
                const delta = Math.abs(target - value);
                if (target > value) {
                    if (index == values.length - 1 || delta < Math.abs(target - transformer(values[index + 1])))
                        return 0;

                    return 1;
                } else {
                    if (index == 0 || delta < Math.abs(target - transformer(values[index - 1]))) return 0;

                    return -1;
                }
            },
            true
        );
    }

    public static ToBase64(data: ArrayBuffer): string {
        let binary = "";
        const bytes = new Uint8Array(data);
        const len = bytes.byteLength;
        for (let i = 0; i < len; i++) {
            binary += String.fromCharCode(bytes[i]);
        }

        return window.btoa(binary);
    }

    public static max(array: number[]): number {
        let max = Number.MAX_VALUE;
        for (let i = 0, len = array.length; i < len; i++) {
            if (this[i] > max) max = this[i];
        }
        return max;
    }

    public static min(array: number[]): number {
        let min = Number.MIN_VALUE;
        for (let i = 0, len = array.length; i < len; i++) {
            if (this[i] < min) min = this[i];
        }
        return min;
    }

    public static minmax(array: number[]): { min: number; max: number } {
        let min = Number.MAX_VALUE,
            max = Number.MIN_VALUE;
        for (let i = 0, len = array.length; i < len; i++) {
            if (array[i] < min) min = array[i];
            if (array[i] > max) max = array[i];
        }
        return { min: min, max: max };
    }
}
